import { CommonModule } from '@angular/common'
import { Component, OnDestroy, OnInit } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { Router } from '@angular/router'
import { Subscription } from 'rxjs'
import { AdminDashboardData, AdminPanelService } from '../../service/admin/admin-panel.service'
import { DdcLoggerService } from '../../service/log/ddc-logger.service'
import { UserService } from '../../service/user-info/user.service'

@Component({
  selector: 'app-ddc-admin-panel',
  templateUrl: './ddc-admin-panel.component.html',
  styleUrls: ['./ddc-admin-panel.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule],
})
export class DdcAdminPanelComponent implements OnInit, OnDestroy {
  // Authentication state
  isAdminAuthenticated = false
  isLoading = false
  showCodeInput = false

  // Code verification
  adminCode = ''
  codeError = ''

  // Dashboard data
  dashboardData: AdminDashboardData | null = null
  dashboardError = ''

  // Session info
  sessionInfo = {
    expiresAt: null as Date | null,
    timeRemaining: '',
  }

  private subscriptions: Subscription[] = []
  private sessionTimer: any

  constructor(
    private adminPanelService: AdminPanelService,
    private logger: DdcLoggerService,
    private userService: UserService,
    private router: Router,
  ) {}

  ngOnInit(): void {
    this.logger.info('DdcAdminPanelComponent: ngOnInit')

    // Check if admin is already authenticated
    const sessionSub = this.adminPanelService.adminSession$.subscribe(session => {
      this.isAdminAuthenticated = session.isAuthenticated
      this.sessionInfo.expiresAt = session.expiresAt

      if (session.isAuthenticated) {
        this.showCodeInput = false
        this.loadDashboardData()
        this.startSessionTimer()
      } else {
        this.showCodeInput = true
        this.dashboardData = null
        this.stopSessionTimer()
      }
    })

    this.subscriptions.push(sessionSub)

    // Initialize based on current state
    if (!this.adminPanelService.isAdminAuthenticated()) {
      this.showCodeInput = true
    } else {
      this.loadDashboardData()
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe())
    this.stopSessionTimer()
  }

  /**
   * Handle admin code verification
   */
  verifyAdminCode(): void {
    if (!this.adminCode || this.adminCode.length !== 6) {
      this.codeError = 'Please enter a valid 6-digit code'
      return
    }

    this.isLoading = true
    this.codeError = ''

    const verifySub = this.adminPanelService.verifyAdminCode(this.adminCode).subscribe({
      next: response => {
        this.isLoading = false
        if (response.success) {
          this.logger.info('Admin code verified successfully')
          this.adminCode = ''
          this.showCodeInput = false
          // Dashboard data will be loaded automatically via session subscription
        } else {
          this.codeError = response.message || 'Invalid admin code'
        }
      },
      error: error => {
        this.isLoading = false
        this.logger.error('Admin code verification error:', error)

        if (error.status === 403) {
          this.codeError = 'Access denied. Admin privileges required.'
        } else if (error.status === 401) {
          this.codeError = 'Invalid admin code. Please try again.'
        } else {
          this.codeError = 'An error occurred. Please try again.'
        }
      },
    })

    this.subscriptions.push(verifySub)
  }

  /**
   * Load admin dashboard data
   */
  loadDashboardData(): void {
    this.isLoading = true
    this.dashboardError = ''

    const dashboardSub = this.adminPanelService.getDashboardData().subscribe({
      next: data => {
        this.isLoading = false
        this.dashboardData = data
        this.logger.info('Dashboard data loaded successfully')
      },
      error: error => {
        this.isLoading = false
        this.logger.error('Dashboard data loading error:', error)

        if (error.status === 401) {
          this.dashboardError = 'Session expired. Please re-authenticate.'
          this.showCodeInput = true
        } else {
          this.dashboardError = 'Failed to load dashboard data. Please try again.'
        }
      },
    })

    this.subscriptions.push(dashboardSub)
  }

  /**
   * Logout from admin session
   */
  logoutAdmin(): void {
    this.isLoading = true

    const logoutSub = this.adminPanelService.logoutAdmin().subscribe({
      next: () => {
        this.isLoading = false
        this.logger.info('Admin logged out successfully')
        this.showCodeInput = true
        this.dashboardData = null
        this.adminCode = ''
      },
      error: error => {
        this.isLoading = false
        this.logger.error('Admin logout error:', error)
        // Even if logout fails, clear local state
        this.showCodeInput = true
        this.dashboardData = null
      },
    })

    this.subscriptions.push(logoutSub)
  }

  /**
   * Handle code input changes
   */
  onCodeInput(): void {
    this.codeError = ''

    // Auto-verify when 6 digits are entered
    if (this.adminCode.length === 6) {
      this.verifyAdminCode()
    }
  }

  /**
   * Handle Enter key press in code input
   */
  onCodeKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      this.verifyAdminCode()
    }
  }

  /**
   * Refresh dashboard data
   */
  refreshDashboard(): void {
    this.loadDashboardData()
  }

  /**
   * Navigate back to main app
   */
  goToHome(): void {
    this.router.navigate(['/home'])
  }

  /**
   * Navigate to user management page
   */
  navigateToUserManagement(): void {
    this.router.navigate(['/admin/users'])
  }

  /**
   * Navigate to AI dictionary generation page
   */
  navigateToAI(): void {
    this.router.navigate(['/admin/ai'])
  }

  private startSessionTimer(): void {
    this.updateTimeRemaining()
    this.sessionTimer = setInterval(() => {
      this.updateTimeRemaining()
    }, 1000)
  }

  private stopSessionTimer(): void {
    if (this.sessionTimer) {
      clearInterval(this.sessionTimer)
      this.sessionTimer = null
    }
  }

  private updateTimeRemaining(): void {
    if (this.sessionInfo.expiresAt) {
      const now = new Date()
      const timeLeft = this.sessionInfo.expiresAt.getTime() - now.getTime()

      if (timeLeft > 0) {
        const minutes = Math.floor(timeLeft / (1000 * 60))
        const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000)
        this.sessionInfo.timeRemaining = `${minutes}:${seconds.toString().padStart(2, '0')}`
      } else {
        this.sessionInfo.timeRemaining = 'Expired'
        this.stopSessionTimer()
      }
    }
  }
}
